import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { LogEntryService } from '@/services/log-entry-service';
import { WaypointService } from '@/services/waypoint-service';
import { analyzePropertyPatterns } from '@/services/pattern-analysis-service';
import { useWeatherStore } from '@/stores/weather-store';
import type { PatternAnalysisResult } from '@/types/patterns';
import type { CurrentConditions } from '@/services/pattern-analysis-service';

const logEntryService = new LogEntryService(db);
const waypointService = new WaypointService(db);

const TTL_MS = 15 * 60 * 1000; // 15 minutes

interface CachedResult {
  data: PatternAnalysisResult;
  fetchedAt: number;
}

interface PatternState {
  results: Map<string, CachedResult>;
  loading: Set<string>;
  errors: Map<string, string>;

  getInsights: (propertyId: string, propertyName?: string) => PatternAnalysisResult | null;
  refreshInsights: (propertyId: string, propertyName?: string) => Promise<void>;
  _invalidateCache: (propertyId: string) => void;
}

export const usePatternStore = create<PatternState>((set, get) => ({
  results: new Map(),
  loading: new Set(),
  errors: new Map(),

  getInsights: (propertyId, propertyName = '') => {
    const { results, loading } = get();

    // Return cached result if within TTL
    const cached = results.get(propertyId);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
      return cached.data;
    }

    // Trigger async refresh if not already in flight
    if (!loading.has(propertyId)) {
      void get().refreshInsights(propertyId, propertyName);
    }

    // Return stale data if available while refreshing
    return cached?.data ?? null;
  },

  refreshInsights: async (propertyId, propertyName = '') => {
    const { loading } = get();
    if (loading.has(propertyId)) return;

    set((s) => {
      const newLoading = new Set(s.loading);
      newLoading.add(propertyId);
      const newErrors = new Map(s.errors);
      newErrors.delete(propertyId);
      return { loading: newLoading, errors: newErrors };
    });

    try {
      const [entries, locations] = await Promise.all([
        logEntryService.getEntriesByProperty(propertyId),
        waypointService.getWaypoints(propertyId),
      ]);

      // Get current conditions from weather store — may be null if unavailable
      const weatherStore = useWeatherStore.getState();
      const conditions = weatherStore.getConditions(propertyId);

      let currentConditions: CurrentConditions | null = null;
      if (conditions) {
        currentConditions = {
          temp: conditions.temp,
          pressureTrend: conditions.pressureTrend ?? 'steady',
          now: new Date(),
        };
      }

      const result = analyzePropertyPatterns(
        propertyId,
        propertyName,
        entries,
        locations,
        currentConditions,
      );

      set((s) => {
        const newResults = new Map(s.results);
        newResults.set(propertyId, { data: result, fetchedAt: Date.now() });
        const newLoading = new Set(s.loading);
        newLoading.delete(propertyId);
        return { results: newResults, loading: newLoading };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze patterns';
      console.error('[pattern-store] refreshInsights:', err);
      set((s) => {
        const newLoading = new Set(s.loading);
        newLoading.delete(propertyId);
        const newErrors = new Map(s.errors);
        newErrors.set(propertyId, message);
        return { loading: newLoading, errors: newErrors };
      });
    }
  },

  _invalidateCache: (propertyId) => {
    set((s) => {
      const newResults = new Map(s.results);
      newResults.delete(propertyId);
      return { results: newResults };
    });
  },
}));

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface PropertyInsightsState {
  insights: PatternAnalysisResult | null;
  dataQuality: PatternAnalysisResult['dataQuality'] | null;
  isLoading: boolean;
  error: string | null;
}

export function usePropertyInsights(
  propertyId: string | null | undefined,
  propertyName?: string,
): PropertyInsightsState {
  const { loading, errors, getInsights } = usePatternStore();

  if (!propertyId) {
    return { insights: null, dataQuality: null, isLoading: false, error: null };
  }

  // Calling getInsights triggers a refresh if needed (side-effectful selector)
  const insights = getInsights(propertyId, propertyName);

  return {
    insights,
    dataQuality: insights?.dataQuality ?? null,
    isLoading: loading.has(propertyId),
    error: errors.get(propertyId) ?? null,
  };
}
